'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { regionFormat } from '@/lib/region-format';
import { timezoneAbbreviation } from '@/lib/timezone';
import { CONTROL_CLASS } from './input';
import { useAnchoredPanel } from './use-anchored-panel';
import { Button } from './button';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function monthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  // Monday-first grid.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** 24h wall-clock string "HH:MM" — the normalized value TimeField emits. */
function fmtTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "HH:MM" -> minutes since midnight, or null for anything else (e.g. ''). */
function timeToMinutes(hhmm: string): number | null {
  const parts = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!parts) return null;
  const h = Number(parts[1]);
  const m = Number(parts[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

/** Region-clock display ("2:30 PM" / "14:30") from a "HH:MM" 24h string; '' for empty. Exported for the display tests. */
export function displayTime(hhmm: string): string {
  const minutes = timeToMinutes(hhmm);
  return minutes === null ? '' : regionFormat.formatClock(Math.floor(minutes / 60), minutes % 60);
}

/** "In 1h" style presets: set date AND time relative to now (rounded to 5 min). */
const QUICK_PRESETS: { label: string; minutes: number }[] = [
  { label: 'In 1h', minutes: 60 },
  { label: 'In 2h', minutes: 120 },
  { label: 'In 4h', minutes: 240 },
  { label: 'In 1d', minutes: 60 * 24 },
  { label: 'In 2d', minutes: 60 * 48 },
  { label: 'In 1w', minutes: 60 * 24 * 7 },
];

const TIME_CHIPS = ['08:00', '10:00', '12:00', '14:00', '16:00'];

function chip(active = false): string {
  return cn(
    'corner-brand-xs border px-2 py-1 text-sm font-medium transition-colors',
    active
      ? 'border-transparent bg-accent text-white'
      : 'border-zinc-700 bg-zinc-900 text-zinc-100 hover:border-zinc-500 hover:bg-zinc-800',
    'disabled:cursor-not-allowed disabled:opacity-50'
  );
}

/** Read-only time display — the box that used to be the text input. */
function TimeDisplay({ value, className }: { value: string; className?: string }) {
  const minutes = timeToMinutes(value);
  return (
    <span
      aria-live="polite"
      className={cn(
        'corner-brand-sm flex h-8 min-w-[6rem] items-center justify-center border border-zinc-700 bg-zinc-900 px-2 text-sm tabular-nums text-zinc-100',
        className
      )}
    >
      {minutes === null ? <span className="text-zinc-500">--:--</span> : displayTime(value)}
    </span>
  );
}

/**
 * Structured time control: the time read-out with −1h/−10m/+10m/+1h steppers and
 * the same quick preset-time chips as DateTimePicker, for surfaces outside the
 * picker. There is deliberately NO free-text entry — every reachable state is a
 * valid "HH:MM", so an ambiguous or unparseable time cannot exist. (The previous
 * text-input version silently kept its prefilled default when typed text failed
 * to parse, which scheduled warm callbacks at "now + 1h" instead of the
 * asked-for time.)
 */
export function TimeField({
  value,
  onChange,
  disabled,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const minutes = timeToMinutes(value);
  const step = (delta: number) => {
    const now = new Date();
    const base = minutes ?? now.getHours() * 60 + now.getMinutes();
    // Clamp to the wall-clock day: stepping never wraps past midnight, because for
    // same-day surfaces (warm callbacks) a silent wrap would change the meaning.
    const next = Math.min(Math.max(base + delta, 0), 24 * 60 - 1);
    onChange(`${String(Math.floor(next / 60)).padStart(2, '0')}:${String(next % 60).padStart(2, '0')}`);
  };
  return (
    <span className={cn('inline-flex flex-col gap-1', className)}>
      <span className="flex items-center gap-1">
        <button type="button" className={chip()} disabled={disabled} onClick={() => step(-60)}>
          −1h
        </button>
        <button type="button" className={chip()} disabled={disabled} onClick={() => step(-10)}>
          −10m
        </button>
        <TimeDisplay value={value} className="flex-1" />
        <button type="button" className={chip()} disabled={disabled} onClick={() => step(10)}>
          +10m
        </button>
        <button type="button" className={chip()} disabled={disabled} onClick={() => step(60)}>
          +1h
        </button>
      </span>
      <span className="flex flex-wrap items-center gap-1">
        {TIME_CHIPS.map((t) => (
          <button
            key={t}
            type="button"
            className={chip(value === t)}
            disabled={disabled}
            onClick={() => onChange(t)}
          >
            {displayTime(t)}
          </button>
        ))}
      </span>
    </span>
  );
}

export function MonthCalendar({
  selected,
  onPick,
  minDate,
  rangeStart,
  rangeEnd,
  onHoverDay,
}: {
  selected: Date | null;
  onPick: (day: Date) => void;
  minDate?: Date;
  /** Visual range selection: endpoints render solid, days between render tinted. */
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  onHoverDay?: (day: Date | null) => void;
}) {
  const today = startOfDay(new Date());
  const [view, setView] = useState<{ y: number; m: number }>(() => {
    const base = selected ?? today;
    return { y: base.getFullYear(), m: base.getMonth() };
  });
  useEffect(() => {
    if (selected) setView({ y: selected.getFullYear(), m: selected.getMonth() });
  }, [selected]);

  const days = useMemo(() => monthGrid(view.y, view.m), [view]);
  const monthLabel = regionFormat.formatDate(new Date(view.y, view.m, 1), {
    month: 'long',
    year: 'numeric',
  });
  const min = minDate ? startOfDay(minDate) : null;

  const shift = (delta: number) => {
    setView(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  return (
    <div className="w-[15.5rem] select-none">
      <div className="flex items-center justify-between px-1 pb-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => shift(-1)}
          className="corner-brand-xs p-1 text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-100"
        >
          <ChevronLeft className="size-3.5" aria-hidden />
        </button>
        <span className="text-xs font-semibold text-zinc-100">{monthLabel}</span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => shift(1)}
          className="corner-brand-xs p-1 text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-100"
        >
          <ChevronRight className="size-3.5" aria-hidden />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {WEEKDAYS.map((d) => (
          <span key={d} className="pb-1 text-center text-xs text-zinc-500">
            {d}
          </span>
        ))}
        {days.map((day) => {
          const outside = day.getMonth() !== view.m;
          const isToday = sameDay(day, today);
          const belowMin = min !== null && day < min;
          const start = rangeStart ? startOfDay(rangeStart) : null;
          const end = rangeEnd ? startOfDay(rangeEnd) : null;
          const isEndpoint =
            (start !== null && sameDay(day, start)) || (end !== null && sameDay(day, end));
          const inRange =
            start !== null && end !== null && day > (start < end ? start : end) && day < (start < end ? end : start);
          const isSelected = (selected !== null && sameDay(day, selected)) || isEndpoint;
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={belowMin}
              onClick={() => onPick(day)}
              onMouseEnter={onHoverDay ? () => onHoverDay(day) : undefined}
              onMouseLeave={onHoverDay ? () => onHoverDay(null) : undefined}
              className={cn(
                'corner-brand-xs mx-auto flex size-7 items-center justify-center text-xs tabular-nums transition-colors',
                isSelected
                  ? 'bg-accent font-semibold text-white'
                  : cn(
                      inRange ? 'bg-accent/20 text-zinc-100' : 'hover:bg-white/5',
                      outside ? 'text-zinc-500' : 'text-zinc-200',
                      isToday && !inRange && 'text-accent-bright'
                    ),
                belowMin && 'cursor-not-allowed opacity-30 hover:bg-transparent'
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface DateTimePickerProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  /** Include a time-of-day field (default true). */
  withTime?: boolean;
  minDate?: Date;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /**
   * The timezone the wall-clock value is in, shown on the trigger and panel
   * (e.g. "CDT"). Defaults to the browser zone's abbreviation. Pass the
   * contact/campaign zone label when the value is converted via lib/timezone.
   */
  timezoneLabel?: string;
  className?: string;
}

/** The one date(+time) control. Local wall-clock; callers convert zones via lib/timezone. */
export function DateTimePicker({
  value,
  onChange,
  withTime = true,
  minDate,
  placeholder = 'Pick a date',
  disabled,
  clearable = false,
  timezoneLabel,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const panelStyle = useAnchoredPanel(open, rootRef, panelRef, close);
  const tz = timezoneLabel ?? timezoneAbbreviation(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const display = value
    ? regionFormat.formatDate(value) +
      (withTime ? ` · ${displayTime(fmtTime(value))} ${tz}` : '')
    : null;

  const pickDay = (day: Date) => {
    const next = new Date(day);
    if (withTime) {
      const base = value ?? new Date();
      next.setHours(base.getHours(), base.getMinutes(), 0, 0);
    }
    onChange(next);
    if (!withTime) setOpen(false);
  };

  // TimeField and the chips only ever emit a valid "HH:MM" — there is no text
  // entry anywhere, so no parse/invalid path exists.
  const setTime = (t: string) => {
    const minutes = timeToMinutes(t);
    if (minutes === null) return;
    const base = value ?? new Date();
    const next = new Date(base);
    next.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    onChange(next);
  };

  const applyPreset = (minutes: number) => {
    const next = new Date(Date.now() + minutes * 60_000);
    // Round up to the next 5 minutes so presets read clean.
    next.setMinutes(Math.ceil(next.getMinutes() / 5) * 5, 0, 0);
    onChange(next);
  };

  const nudge = (deltaMinutes: number) => {
    const base = value ?? new Date();
    onChange(new Date(base.getTime() + deltaMinutes * 60_000));
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(CONTROL_CLASS, 'flex items-center gap-2 text-left')}
      >
        <Calendar className="size-3.5 shrink-0 text-zinc-400" aria-hidden />
        {display ? (
          <span className="truncate tabular-nums">{display}</span>
        ) : (
          <span className="truncate text-zinc-500">{placeholder}</span>
        )}
      </button>
      {open && panelStyle
        ? createPortal(
        <div ref={panelRef} style={panelStyle} className="corner-brand-sm border border-zinc-700 bg-zinc-900 p-3 shadow-xl shadow-black/40">
          {withTime ? (
            <div className="mb-2 flex flex-wrap gap-1 border-b border-zinc-800 pb-2">
              {QUICK_PRESETS.map((p) => (
                <button key={p.label} type="button" className={chip()} onClick={() => applyPreset(p.minutes)}>
                  {p.label}
                </button>
              ))}
            </div>
          ) : null}
          <MonthCalendar selected={value} onPick={pickDay} minDate={minDate} />
          {withTime ? (
            <div className="mt-2 grid gap-2 border-t border-zinc-800 pt-2">
              <div className="flex items-center gap-1">
                <button type="button" className={chip()} onClick={() => nudge(-60)}>
                  −1h
                </button>
                <button type="button" className={chip()} onClick={() => nudge(-10)}>
                  −10m
                </button>
                <TimeDisplay value={value ? fmtTime(value) : ''} className="mx-auto" />
                <button type="button" className={chip()} onClick={() => nudge(10)}>
                  +10m
                </button>
                <button type="button" className={chip()} onClick={() => nudge(60)}>
                  +1h
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {TIME_CHIPS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={chip(Boolean(value && fmtTime(value) === t))}
                    onClick={() => setTime(t)}
                  >
                    {displayTime(t)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="mt-2 flex items-center gap-2 border-t border-zinc-800 pt-2">
            <span className="text-xs text-zinc-400">Times in {tz}</span>
            <span className="ml-auto flex items-center gap-1">
              {clearable && value ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  Clear
                </Button>
              ) : null}
              <Button size="sm" variant="primary" onClick={() => setOpen(false)}>
                Done
              </Button>
            </span>
          </div>
        </div>,
        document.body)
        : null}
    </div>
  );
}
